import React, {useState, useEffect, useMemo} from "react";
import {
    Box,
    Grid,
    Typography,
    Card,
    CardActionArea,
    CardMedia,
    CardContent,
    Button,
    IconButton, FormControlLabel, Checkbox,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import InfoDialog from "./InfoDialog";
import { useFurnitureStore } from "../../../contexts/SelectedVariantContext";
import {
    variants,
    macroCategories,
    pancheSubCategories,
    pannelliGrigliatiSubCategories,
    pergodominoSubCategories,
    telaiSubCategories,
} from "./furnitureVariants";
import {defaultFinishByMaterial} from "../../../utils/colors";
import { useTool } from "../../../contexts/ToolContext";
import { useTranslation } from "react-i18next";

const MainCard = styled(Card)(({ theme }) => ({
    border: "none",
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    textAlign: "center",
    cursor: "pointer",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
    transition: "transform 0.3s, box-shadow 0.3s",
    position: "relative",
    overflow: "hidden",
    "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background:
            "linear-gradient(135deg, rgba(255,255,255,0.3), rgba(0,0,0,0.1))",
        opacity: 0,
        transition: "opacity 0.3s",
    },
    "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: theme.shadows[6],
        "&::after": {
            opacity: 1,
        },
    },
}));

const VariantCard = styled(Card)(({ theme, selected }) => ({
    border: selected
        ? `2px solid ${theme.palette.primary.main}`
        : "1px solid transparent",
    borderRadius: theme.shape.borderRadius,
    boxShadow: selected ? theme.shadows[6] : theme.shadows[3],
    transition: "transform 0.2s, box-shadow 0.2s, border 0.2s",
    "&:hover": {
        transform: "scale(1.03)",
        boxShadow: theme.shadows[5],
    },
}));

const categoryIconStyles = {
    width: 90,
    height: 90,
    objectFit: "cover",
    mb: 1
};

const cardActionAreaStyles = {
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
};

export function FurnitureSelector({ onVariantSelect, onNavigationChange }) {
    const [selectedMacro, setSelectedMacro] = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);

    // Notify parent about navigation state changes
    useEffect(() => {
        if (onNavigationChange) {
            onNavigationChange(selectedMacro || selectedSubCategory, {
                resetNavigation: () => {
                    setSelectedMacro(null);
                    setSelectedSubCategory(null);
                }
            });
        }
    }, [selectedMacro, selectedSubCategory, onNavigationChange]);
    const selectedVariant = useFurnitureStore((s) => s.selectedVariant);
    const setSelectedVariant = useFurnitureStore((s) => s.setSelectedVariant);
    const resetVariant = useFurnitureStore((s) => s.resetVariant);
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);
    const [infoDialogContent, setInfoDialogContent] = useState({
        title: "",
        content: "",
    });
    const { onChangeTool } = useTool();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        if (onVariantSelect) onVariantSelect(selectedVariant);
    }, [selectedVariant, onVariantSelect]);

    const filteredVariants = useMemo(() => {
        let filteredList = [];

        if (
            selectedMacro === "panche_sedute" ||
            selectedMacro === "telai_ruote" ||
            selectedMacro === "pannelli_grigliati" ||
            selectedMacro === "pergo_domino"
        ) {
            filteredList = variants.filter((v) => v.subCategory === selectedSubCategory);
        } else {
            const mapping = Object.fromEntries(
                macroCategories.map((m) => [m.key, m.categories])
            );
            filteredList = variants.filter((v) =>
                mapping[selectedMacro]?.includes(v.category)
            );
        }

        // Handle pannello_frangivista: show only first variant per size
        if (selectedSubCategory === "pannello_frangivista") {
            const sizeGroups = {};
            filteredList.forEach(v => {
                if (v.size_string && !sizeGroups[v.size_string]) {
                    sizeGroups[v.size_string] = v;
                }
            });
            return Object.values(sizeGroups);
        }

        return filteredList;
    }, [selectedMacro, selectedSubCategory]);

    if (!selectedMacro) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography
                    variant="h5"
                    sx={{ mb: 2, textAlign: "center", fontWeight: "bold" }}
                >
                    {t("accessories_settings.furnishing_select.select_macro")}
                </Typography>
                <Grid container spacing={2} justifyContent="center">
                    {macroCategories.map((macro) => (
                        <Grid item xs={12} sm={6} key={macro.key}>
                            <MainCard onClick={() => setSelectedMacro(macro.key)}>
                                <CardActionArea sx={cardActionAreaStyles}>
                                    {macro.imageUrl && (
                                        <CardMedia
                                            component="img"
                                            image={macro.imageUrl}
                                            alt={macro.label}
                                            sx={categoryIconStyles}
                                        />
                                    )}
                                    <Typography variant="subtitle1" sx={{ fontWeight: "500" }}>
                                        {t(`furniture.categories.${macro.key}`, macro.label)}
                                    </Typography>
                                </CardActionArea>
                            </MainCard>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    if (selectedMacro === "panche_sedute" && !selectedSubCategory) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography
                    variant="h5"
                    sx={{ mb: 2, textAlign: "center", fontWeight: "bold" }}
                >
                    {t("accessories_settings.furnishing_select.select_bench_type")}
                </Typography>
                <Grid container spacing={2} justifyContent="center">
                    {pancheSubCategories.map((sub) => (
                        <Grid item xs={12} sm={6} key={sub.key}>
                            <MainCard onClick={() => setSelectedSubCategory(sub.key)}>
                                <CardActionArea sx={cardActionAreaStyles}>
                                    {sub.imageUrl && (
                                        <CardMedia
                                            component="img"
                                            image={sub.imageUrl}
                                            alt={sub.label}
                                            sx={categoryIconStyles}
                                        />
                                    )}
                                    <Typography variant="subtitle1" sx={{ fontWeight: "500" }}>
                                        {t(`furniture.subcategories.${sub.key}`, sub.label)}
                                    </Typography>
                                </CardActionArea>
                            </MainCard>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    if (selectedMacro === "telai_ruote" && !selectedSubCategory) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography
                    variant="h5"
                    sx={{ mb: 2, textAlign: "center", fontWeight: "bold" }}
                >
                    {t("accessories_settings.furnishing_select.select_frame_type")}
                </Typography>
                <Grid container spacing={2} justifyContent="center">
                    {telaiSubCategories.map((sub) => (
                        <Grid item xs={12} sm={6} key={sub.key}>
                            <MainCard onClick={() => setSelectedSubCategory(sub.key)}>
                                <CardActionArea sx={cardActionAreaStyles}>
                                    {sub.imageUrl && (
                                        <CardMedia
                                            component="img"
                                            image={sub.imageUrl}
                                            alt={sub.label}
                                            sx={categoryIconStyles}
                                        />
                                    )}
                                    <Typography variant="subtitle1" sx={{ fontWeight: "500" }}>
                                        {t(`furniture.subcategories.${sub.key}`, sub.label)}
                                    </Typography>
                                </CardActionArea>
                            </MainCard>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    if (selectedMacro === "pannelli_grigliati" && !selectedSubCategory) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography
                    variant="h5"
                    sx={{ mb: 2, textAlign: "center", fontWeight: "bold" }}
                >
                    {t("accessories_settings.furnishing_select.select_type")}
                </Typography>
                <Grid container spacing={2} justifyContent="center">
                    {pannelliGrigliatiSubCategories.map((sub) => (
                        <Grid item xs={12} sm={6} key={sub.key}>
                            <MainCard onClick={() => setSelectedSubCategory(sub.key)}>
                                <CardActionArea sx={cardActionAreaStyles}>
                                    {sub.imageUrl && (
                                        <CardMedia
                                            component="img"
                                            image={sub.imageUrl}
                                            alt={sub.label}
                                            sx={categoryIconStyles}
                                        />
                                    )}
                                    <Typography variant="subtitle1" sx={{ fontWeight: "500" }}>
                                        {t(`furniture.subcategories.${sub.key}`, sub.label)}
                                    </Typography>
                                </CardActionArea>
                            </MainCard>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    if (selectedMacro === "pergo_domino" && !selectedSubCategory) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography
                    variant="h5"
                    sx={{ mb: 2, textAlign: "center", fontWeight: "bold" }}
                >
                    {t("accessories_settings.furnishing_select.select_pergodomino_type")}
                </Typography>
                <Grid container spacing={2} justifyContent="center">
                    {pergodominoSubCategories.map((sub) => (
                        <Grid item xs={12} sm={6} key={sub.key}>
                            <MainCard onClick={() => setSelectedSubCategory(sub.key)}>
                                <CardActionArea sx={cardActionAreaStyles}>
                                    {sub.imageUrl && (
                                        <CardMedia
                                            component="img"
                                            image={sub.imageUrl}
                                            alt={sub.label}
                                            sx={categoryIconStyles}
                                        />
                                    )}
                                    <Typography variant="subtitle1" sx={{ fontWeight: "500" }}>
                                        {t(`furniture.subcategories.${sub.key}`, sub.label)}
                                    </Typography>
                                </CardActionArea>
                            </MainCard>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }
    const handleVariantSelect = (variant) => {
        const mat = variant.material.toLowerCase();
        const defaultColorId = defaultFinishByMaterial[mat] || "";

        setSelectedVariant({
            ...variant,
            colorId: defaultColorId,
        });
        onChangeTool(null, null, "");
    };


    const handleInfoClick = (e, variant) => {
        e.stopPropagation();
        let dialogData = {
            title: "Informazioni",
            content:
                "Contenuto non disponibile al momento. Sarà aggiornato a breve.",
        };

        if (variant.category === "pergodomino") {
            dialogData = {
                title: "Domino",
                content:
                    "Possibilità di dimensioni personalizzate.\n\n" +
                    "Kit per zavorra:\n" +
                    "• fissaggio con viti autofilettanti\n" +
                    "• piastra universale di fissaggio\n" +
                    "• piastra personalizzata di fissaggio\n\n" +
                    "Riempimento: Ghiaietto 3/6 in sacchi da 20 kg.",
            };
        }
        setInfoDialogContent(dialogData);
        setInfoDialogOpen(true);
    };


    return (
        <>
            <Box sx={{ maxWidth: 1000, margin: "0 auto", p: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Typography variant="h5">
                        {t("accessories_settings.furnishing_select.select_variant")}
                        {" "}
                        {["panche_sedute", "telai_ruote", "pannelli_grigliati", "pergo_domino"].includes(
                            selectedMacro
                        )
                            ? `${
                                (() => {
                                    const subCategory = (selectedMacro === "panche_sedute"
                                        ? pancheSubCategories
                                        : selectedMacro === "telai_ruote"
                                            ? telaiSubCategories
                                            : selectedMacro === "pannelli_grigliati"
                                                ? pannelliGrigliatiSubCategories
                                                : pergodominoSubCategories
                                    ).find((s) => s.key === selectedSubCategory);
                                    return subCategory ? t(`furniture.subcategories.${subCategory.key}`, subCategory.label) : "";
                                })()
                            }`
                            : ""}
                    </Typography>
                </Box>
                <Grid container spacing={3}>
                    {filteredVariants.map((variant) => (
                        <Grid key={variant.id} item xs={12} sm={6}>
                            <CardActionArea onClick={() => handleVariantSelect(variant)}>
                                <VariantCard selected={selectedVariant?.id === variant.id}>
                                    <CardMedia
                                        component="img"
                                        image={variant.imageUrl}
                                        alt={variant.name}
                                        sx={{ width: "100%", aspectRatio: "1", objectFit: "cover" }}
                                    />
                                    <CardContent
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                textAlign: "center",
                                                fontWeight: "bold",
                                                mb: 1,
                                            }}
                                        >
                                            {variant.subCategory === "pannello_frangivista" ? (
                                                <>
                                                    {t(`furniture.subcategories.pannello_frangivista`, "Pannello Frangivista")}
                                                    {" "}
                                                    {variant.size_string}
                                                </>
                                            ) : (
                                                t(`furniture.variants.${variant.id}`, variant.name)
                                            )}
                                        </Typography>
                                        <IconButton
                                            // TODO?: Nascondere il pulsante info se non ci sono info?
                                            size="small"
                                            onClick={(e) => handleInfoClick(e, variant)}
                                            sx={{ p: 0 }}
                                        >
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                    </CardContent>
                                </VariantCard>
                            </CardActionArea>
                        </Grid>
                    ))}
                </Grid>
            </Box>
            {(selectedVariant?.category === "exte" || selectedVariant?.category === "cover" || selectedVariant?.category === "sedute") && (
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={<Checkbox />}
                        label={t("accessories_settings.furnishing_select.add_cushions")}
                        checked={!!selectedVariant?.withCushions}
                        onChange={(event, checked) => {
                            setSelectedVariant({
                                ...selectedVariant,
                                withCushions: checked,
                            });
                            onChangeTool(null, null, "");
                        }}
                    />
                </Box>
            )}
            <InfoDialog
                open={infoDialogOpen}
                onClose={() => setInfoDialogOpen(false)}
                title={infoDialogContent.title}
                content={infoDialogContent.content}
            />
        </>
    );
}
